import * as React from 'react'
import { SearchIcon, CloseIcon } from '@/src/icons'

import { cn } from '@/registry/reapit/lib/utils'
import { inputVariants } from '@/registry/reapit/ui/input'

type SearchInputSize = 'small' | 'medium' | 'large'

type SearchInputProps = Omit<React.ComponentProps<'input'>, 'size' | 'type'> & {
  size?: SearchInputSize
  variant?: 'default' | 'borderless'
  onClear?: () => void
  showClear?: boolean
}

function SearchInput({
  className,
  size = 'medium',
  variant = 'default',
  onClear,
  showClear = true,
  value,
  ...props
}: SearchInputProps) {
  const hasValue = value !== undefined && value !== ''

  return (
    <div
      data-slot="search-input-wrapper"
      data-size={size}
      data-variant={variant}
      className={cn(
        'flex w-full min-w-0 items-center rounded-md transition-[color,box-shadow]',
        variant === 'default' && [
          'border border-input bg-transparent shadow-xs',
          'focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50',
          'has-[input[aria-invalid]]:border-destructive has-[input[aria-invalid]]:ring-destructive/20',
        ],
        variant === 'borderless' && 'border-0 bg-transparent focus-within:ring-0',
      )}
    >
      {/* DS icon-md (20px / size-5) for medium+large; DS icon-xs/sm (14px) for small compact */}
      <SearchIcon
        className={cn(
          'pointer-events-none shrink-0 text-muted-foreground',
          size === 'small' ? 'ml-2.5 size-3.5' : 'ml-3 size-5',
        )}
        aria-hidden="true"
      />
      <input
        type="search"
        data-slot="search-input"
        value={value}
        className={cn(
          inputVariants({ size }),
          'flex-1 border-0 shadow-none outline-none focus-visible:ring-0',
          '[&::-webkit-search-cancel-button]:hidden',
          className,
        )}
        {...props}
      />
      {showClear && hasValue && onClear && (
        <button
          type="button"
          data-slot="search-input-clear"
          onClick={onClear}
          aria-label="Clear search"
          className={cn(
            'shrink-0 rounded-sm text-muted-foreground opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            size === 'small' ? 'mr-2 size-3.5' : 'mr-3 size-5',
          )}
        >
          <CloseIcon className="size-full" />
        </button>
      )}
    </div>
  )
}

export { SearchInput }
export type { SearchInputProps }
